import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";

// Modules
import Reveal from "reveal.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  useDroppable,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
// import RevealDist from 'reveal.js/dist/reveal.esm';
import Notes from "reveal.js/plugin/notes/notes";

// App
import { filesystem, filesystem_root, subdomain, presenterNotesEnabled } from "../../config";
import { getOnlineNode, getExternal } from "../../core/getNode";
import { AssetSigsProvider } from "../../core/assetSigs";
import Slide from "./partials/slide/view/slide";
import RevealControls from "./partials/revealControls";
import {
  jsonParser,
  runAnimation,
  runFragment,
  addWidescreen,
  iframeClickEvents,
  getThemeJson,
} from "../../utils/helpers";
import CompanyappLoader from "../../partials/companyappLoader";
import AudioPrompt from "../../partials/audioPrompt";
import { logCustomEvent } from "../../core/analytics";
import { ViewContextProvider, useViewContext } from "./viewPresentationContext";

// UI components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { Box, Alert } from "@mui/material";
import { debounce } from "lodash";

let isSpeakerView = false;

try {
  isSpeakerView = window.parent?.name === "reveal.js - Notes";
} catch {
  // Cross-origin parent → ignore
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

function ViewPresentationInner(props) {
  const [isLoading, setLoading] = useState(true);
  const [additionalLoader, setAdditionalLoader] = useState(true);
  const [isError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "An error occurred. Please try again or contact system administrator.",
  );

  const { setSlideAudioData } = useViewContext();
  const [isInitial, setIsInitial] = useState(true);

  let [elementDimensions, setElementDimensions] = useState({
    width: 130,
    height: 30,
  });
  let [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // controls delta
  let [x, setX] = useState(window.innerWidth - (elementDimensions.width + 10));
  let [y, setY] = useState(30);

  // hooks
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // redux
  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;

  // refs
  const revealRef = useRef(null);
  // Guards async callbacks (fetches, reveal init, timers) from calling
  // setState after the user navigates away — the source of the "state update
  // on an unmounted component" leak warnings.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const [presentation, setPresentation] = useState(null);
  const [audio, setAudio] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [reveal, setReveal] = useState(null);
  const [indexh, setIndexH] = useState(0);
  const [showControls, setShowControls] = useState(true);
  // const [indexv, setIndexV] = useState(0);

  /** Presentation audio */
  const [playSlideAudio, setPlaySlideAudio] = useState(false);
  const [slideHasAudioPrompt, setSlideHasAudioPrompt] = useState(false);

  const hash = searchParams.get("hash");
  const slideIds = searchParams.get("slide_ids");
  const noControls = searchParams.get("nocontrols");
  const previewChanges = searchParams.get("previewChanges");
  const previewChangesSlideId = searchParams.get("previewChangesSlideId");
  const previewChangesAnalysisId = searchParams.get("previewChangesAnalysisId");

  // droppable
  const { setNodeRef } = useDroppable({
    id: "droppable",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleSlideAudioPrompt = useCallback(() => {
    setSlideHasAudioPrompt(true);
  }, []);

  // Toggle nav sidebar and getcontent
  useEffect(() => {
    dispatch({
      type: "TOGGLE_NAVIGATION",
      payload: {
        showNavigation: false,
        manualToggle: true,
      },
    });
    getContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize listener
  useEffect(() => {
    setDimensions(getWindowDimensions()); // Necessary to make sure dimensions are set upon initial load

    function handleResize() {
      setDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mouse move listener
  useEffect(() => {
    const handleMouseMove = debounce((event) => {
      setShowControls(true);
      hideRevealControls();
    });
    const hideRevealControls = debounce(() => {
      setShowControls(false);
    }, 5000);

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "next") {
        revealRef.current.next();
        return;
      }

      if (event.data === "previous") {
        revealRef.current.prev();
        return;
      }

      if (event.data?.type === "SLIDE_READY") {
        const iframe = [...document.querySelectorAll("iframe")].find(
          (f) => f.contentWindow === event.source,
        );

        if (iframe) {
          iframe.dataset.slideReady = "true";

          const currentSlide = revealRef.current?.getCurrentSlide();
          const currentIframe = currentSlide?.querySelector("iframe.advanced-slide");

          // If this iframe is on the active slide, tell it to run
          if (currentIframe === iframe) {
            iframe.contentWindow.postMessage({ type: "RUN_ANIMATION" }, "*");
          }
        }

        return;
      }

      if (event.data?.type === "REGISTER_FRAGMENTS") {
        const iframe = [...document.querySelectorAll("iframe")].find(
          (f) => f.contentWindow === event.source,
        );

        registerFragments(iframe, event.data.count)

        return;
      }

      // console.log("Unknown message:", event);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [revealRef]);

  // Initialize Plyr on slide videos, and tear the instances down on unmount.
  // Fixes vs the previous version, which leaked a player per video:
  //  - Select `video.plyr`, not `.plyr`: Plyr wraps each <video> in a .plyr
  //    container, so a bare ".plyr" selector re-matched the wrapper and
  //    double-initialised every video.
  //  - Mark data-initialized BEFORE constructing Plyr so the observer can't
  //    re-enter on Plyr's own DOM mutations.
  //  - Keep a registry and destroy() every instance on cleanup (the old code
  //    never destroyed them — listeners + detached DOM leaked for the session).
  //  - Scope the observer to the slides container instead of document.body so
  //    it doesn't re-scan the whole deck on every unrelated mutation.
  useEffect(() => {
    if (!reveal) return;

    const slidesRoot = document.querySelector(".reveal .slides");
    if (!slidesRoot) return;

    const players = [];

    const initVideos = () => {
      slidesRoot
        .querySelectorAll("video.plyr:not([data-initialized])")
        .forEach((video) => {
          video.setAttribute("data-initialized", "true");
          players.push(
            new window.Plyr(video, {
              controls: [
                "play-large",
                "play",
                "progress",
                "current-time",
                "mute",
                "volume",
                "fullscreen",
              ],
            }),
          );
        });
    };

    const observer = new MutationObserver(initVideos);
    observer.observe(slidesRoot, { childList: true, subtree: true });
    initVideos();

    return () => {
      observer.disconnect();
      players.forEach((player) => {
        try {
          player.destroy();
        } catch (e) {
          /* element already removed */
        }
      });
    };
  }, [reveal]);

  // Slide jump
  useEffect(() => {
    if (reveal) {
      revealRef.current = reveal;
    }

    window.goToSlide = goToSlide;
    return () => {
      window.goToSlide = undefined;
    };
    // eslint-disable-next-line
  }, [reveal]);

  // Initialize reveal
  useEffect(() => {
    if (
      presentation &&
      presentation?.slides &&
      presentation.slides.length > 0 &&
      !reveal
    ) {
      const previousTitle = document.title;

      if (presentation?.title) {
        document.title = `${presentation.title} (${presentation.id}) – ${previousTitle}`;
      }

      // Small delay to ensure slides are rendered
      const timer = setTimeout(() => {
        initReveal();
      }, 100);

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation, reveal]);

  // Check if any slide in the presentation has audio (for the prompt)
  useEffect(() => {
    if (isSpeakerView || !presentation?.slides?.length) {
      return;
    }

    const presentationJson = jsonParser(presentation?.json);

    if (presentationJson?.slideAudio) {
      const hasAnySlideAudio = presentation.slides.some(
        (slide) => presentationJson.slideAudio[slide.id],
      );

      if (hasAnySlideAudio) {
        handleSlideAudioPrompt();
      }
    }
  }, [presentation, handleSlideAudioPrompt]);

  // Monitor current slide audio
  useEffect(() => {
    if (isSpeakerView || !presentation?.slides?.length) {
      return;
    }

    const presentationJson = jsonParser(presentation?.json);
    const currentSlide = presentation?.slides?.[indexh];

    if (currentSlide && presentationJson?.slideAudio) {
      const slideAudio = presentationJson.slideAudio[currentSlide.id];

      if (slideAudio) {
        setSlideAudioData(slideAudio);
      } else {
        setSlideAudioData(null);
      }
    } else {
      setSlideAudioData(null);
    }
  }, [indexh, presentation, setSlideAudioData]);

  const goToSlide = (slideId) => {
    let slideNum = presentation?.slides.map((e) => e.id).indexOf(slideId);
    reveal.slide(parseInt(slideNum));
  };

  const getContent = () => {
    if (params?.id) {
      if (props.preview) {
        getContentFromApi();
      } else {
        checkAuth();
      }
    } else {
      setError(true);
      setAdditionalLoader(false);
    }
  };

  const getContentFromApi = () => {
    // let path = `api/presentation/${params.id}?status=${props.preview ? 'false' : 'true'}&slide_status=true`;
    let path = `api/presentation/${params.id}?status=false&slide_status=true`;

    if (slideIds) {
      path += `&slide_ids=${slideIds}`;
    }

    if (hash) {
      path += `&auHash=${hash}`;
    }

    getOnlineNode(path)
      .then((response) => {
        if (!mountedRef.current) return;
        if (response.data) {
          const json = response.data.json;

          if (json?.audio) {
            setAudio(json.audio);
          }

          if (json?.backgroundColor) {
            setBackgroundColor(json.backgroundColor);
          }

          if (previewChanges && previewChangesSlideId) {
            let path = `/api/v2/ai/slide/responses?slide_id=${previewChangesSlideId}&show=1`;

            if (previewChangesAnalysisId) {
              path += `&analysis_id=${previewChangesAnalysisId}`;
            }

            getOnlineNode(path)
              .then((aIresponseData) => {
                if (!mountedRef.current) return;
                if (aIresponseData?.data?.rows?.length) {
                  const aiResponse = aIresponseData.data.rows[0];

                  try {
                    const parsed = JSON.parse(aiResponse.response);
                    let presData = { ...response.data };
                    let slides = [];

                    // Convert previewChangesSlideId to number for comparison
                    const targetSlideId = parseInt(previewChangesSlideId, 10);

                    presData.slides.forEach((slide) => {
                      // Use loose equality or convert both to same type
                      if (parseInt(slide.id, 10) === targetSlideId) {
                        slides.push({
                          ...slide,
                          // Ensure slide_json is a string for jsonParser
                          slide_json: typeof parsed.suggested_json === 'string'
                            ? parsed.suggested_json
                            : JSON.stringify(parsed.suggested_json),
                        });
                      }
                    });

                    if (slides.length > 0) {
                      presData.slides = slides;
                      presData.slide_length = slides.length;
                      setPresentation(presData);
                    } else {
                      // No matching slide found, show original presentation
                      console.error("Preview slide not found in presentation");
                      setPresentation(response.data);
                    }
                  } catch (e) {
                    console.error("Error parsing AI response:", e);
                    setPresentation(response.data);
                  }
                } else {
                  setPresentation(response.data);
                }
              })
              .catch((error) => {
                if (!mountedRef.current) return;
                console.error("Error fetching AI analysis:", error);
                setPresentation(response.data);
              })
              .finally(() => {
                if (!mountedRef.current) return;
                setError(false);
                setLoading(false);
              });
          } else {
            setPresentation(response.data);

            setError(false);
            setLoading(false);
          }
        } else {
          setError(true);
          setAdditionalLoader(false);
        }
      })
      .catch((_error) => {
        if (!mountedRef.current) return;
        if (_error?.response?.status === 403) {
          setErrorMessage("You do not have access to view this presentation.");
        }

        setLoading(false);
        setError(true);
        setAdditionalLoader(false);
      });
  };

  const getContentFromStaticFS = () => {
    let path = `${filesystem_root}/presentation_book.php?presentation_id=${params.id}&subdomain=${subdomain}&filesystem=${filesystem}`;

    if (slideIds) {
      path += `&slide_ids=${slideIds}`;
    }

    getExternal(path, "get")
      .then((response) => {
        if (!mountedRef.current) return;
        if (response.data) {
          if (!user.current_user) {
            let hasMatch = false;
            if (response.data?.access_token.length > 0) {
              response.data.access_token.forEach((access_token) => {
                if (access_token.token === hash) {
                  hasMatch = true;
                }
              });
            } else {
              hasMatch = false;
            }

            // check hash
            if (hasMatch) {
              logCustomEvent(
                "read",
                {
                  title: response.data.title,
                  nid: response.data.id,
                },
                "Presentation",
              );
              renderPresentation(response);
            } else {
              setErrorMessage(
                "You do not have access to view this presentation.",
              );
              setError(true);
              setLoading(false);
              setAdditionalLoader(false);
            }
          } else {
            logCustomEvent(
              "read",
              {
                title: response.data.title,
                nid: response.data.id,
              },
              "Presentation",
            );

            renderPresentation(response);
          }
        } else {
          setError(true);
          setLoading(false);
          setAdditionalLoader(false);
        }
      })
      .catch((_error) => {
        if (!mountedRef.current) return;
        setError(true);
        setLoading(false);
        setAdditionalLoader(false);
      });
  };

  const registerFragments = (iframe, count) => {

        if (!iframe) return;

        const slide = iframe.closest("section");
        if (!slide) return;


        if (!Number.isInteger(count) || count <= 0) return;

        slide
          .querySelectorAll('.fragment[data-iframe-fragment="true"]')
          .forEach((el) => el.remove());

        // Append invisible fragments after the iframe
        for (let i = 1; i <= count; i++) {
          const fragment = document.createElement("span");
          fragment.className = "fragment";
          fragment.dataset.iframeFragment = "true";
          fragment.dataset.step = String(i);
          fragment.style.display = "none";

          iframe.insertAdjacentElement("beforebegin", fragment);
        }

        return;
      }

  const renderPresentation = (response) => {
    const json = jsonParser(response.data.json);

    if (json?.audio) {
      setAudio(json.audio);
    }

    if (json?.backgroundColor) {
      setBackgroundColor(json.backgroundColor);
    }

    setPresentation(response.data);
    setError(false);
    setLoading(false);
  };

  const checkAuth = () => {
    if (hash) {
      getContentFromStaticFS();
    } else {
      if (user.current_user) {
        getContentFromStaticFS();
      } else {
        setErrorMessage("You do not have access to view this presentation.");
        setLoading(false);
        setError(true);
        setAdditionalLoader(false);
      }
    }
  };

  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  // Store original BEFORE any patching
  const originalScrollIntoView = Element.prototype.scrollIntoView;

  const initReveal = (index) => {
    let revealSettings = {
      width: 1366,
      height: 768,
      margin: 0,
      slideNumber: false,
      help: false,
      transition: "slide",
      progress: false,
      hashOneBasedIndex: true,
      respondToHashChanges: isInIframe ? false : true,
      hash: isInIframe ? false : true,
      history: isInIframe ? false : true,
      controls: false,
      controlsTutorial: false,
      overview: false,
      fragmentInURL: isInIframe ? false : true,
      autoSlide: 0,
      viewDistance: 10,
      preloadIframes: true,
      maxScale: 5.0,
      embedded: isInIframe,
    };

    if (!hash && !isInIframe && permissions.access_note_features && presenterNotesEnabled) {
      revealSettings.plugins = [Notes];
    }

    let deck = new Reveal(revealSettings);

    deck
      .initialize()
      .then(() => {
        if (!mountedRef.current) return;
        setTimeout(() => {
          if (!mountedRef.current) return;
          setAdditionalLoader(false);
          checkAdvancedSlide(deck.getCurrentSlide());
        }, 2500);

        setReveal(deck);

        if (index) {
          deck.slide(index);
        }

        const indices = deck.getIndices();

        if (indices && indices.h) {
          setIndexH(indices.h);
        }

        // Only patch navigation if in iframe
        if (isInIframe) {
          ["prev", "next", "slide"].forEach((method) => {
            const original = deck[method].bind(deck);
            deck[method] = function (...args) {
              Element.prototype.scrollIntoView = function () {};
              return original(...args);
            };
          });

          deck.on("slidechanged", (event) => {
            requestAnimationFrame(() => {
              Element.prototype.scrollIntoView = originalScrollIntoView;
            });

            setIndexH(event.indexh);
            checkAdvancedSlide(deck.getCurrentSlide());

            const allIframes = document.querySelectorAll(
              ".slides iframe.advanced-slide",
            );
            allIframes.forEach((iframe) => {
              addWidescreen(iframe);
            });
          });
        } else {
          // Non-iframe slidechanged handler
          deck.on("slidechanged", (event) => {
            setIndexH(event.indexh);
            checkAdvancedSlide(deck.getCurrentSlide());

            const allIframes = document.querySelectorAll(
              ".slides iframe.advanced-slide",
            );
            allIframes.forEach((iframe) => {
              addWidescreen(iframe);
            });
          });
        }

        deck.on("slidetransitionend", (event) => {
          const allIframes = document.querySelectorAll(
            ".slides iframe.advanced-slide",
          );
          allIframes.forEach((iframe) => {
            addWidescreen(iframe);
          });
        });
        deck.on("fragmentshown", (event) => {
          runAdvancedSlideFragment(
            deck.getCurrentSlide(),
            event.fragment.dataset.step,
          );
        });
        deck.on("fragmenthidden", (event) => {
          runAdvancedSlideFragment(
            deck.getCurrentSlide(),
            event.fragment.dataset.step - 1,
          );
        });
      })
      .catch((error) => {
        setAdditionalLoader(false);
      });
  };

  const checkAdvancedSlide = (currentSlide) => {
    const allIframes = document.querySelectorAll(
      ".slides iframe.advanced-slide",
    );
    allIframes.forEach((iframe) => {
      addWidescreen(iframe);
    });

    let currentIframe;

    if (currentSlide) {
      try {
        currentIframe = currentSlide.querySelector("iframe.advanced-slide");
      } catch {
        //
      }
    }

    if (currentIframe) {
      if (currentIframe.dataset.slideReady === "true") {
        // New slide with message passing - tell it to run directly
        currentIframe.contentWindow.postMessage({ type: "RUN_ANIMATION" }, "*");
      } else {
        // Legacy slide - fall back to polling
        runAnimation(currentIframe);
      }
      iframeClickEvents(currentIframe);
    }
  };

  const runAdvancedSlideFragment = (currentSlide, fragmentStep) => {
    let currentIframe;
    if (currentSlide) {
      try {
        currentIframe = currentSlide.querySelector("iframe.advanced-slide");
      } catch {
        //
      }
    }
    if (currentIframe) {
      runFragment(currentIframe, fragmentStep);
    }
  };

  function handleDragEnd(ev) {
    setX((x += ev.delta.x));
    setY((y += ev.delta.y));
  }

  const memoizedSlides = useMemo(() => {
    const isLive = props.preview ? false : true;

    if (
      !presentation ||
      !presentation?.slides ||
      presentation?.slides?.length < 1
    )
      return null;

    const presentationJson = jsonParser(presentation?.json);

    return presentation.slides.map((slide, index) => {
      // const slideNotes = notesObj?.[slide.id] ?? null;
      const json = jsonParser(slide.slide_json);
      const theme_json = getThemeJson(slide.theme.label);
      const themeDirectory = isLive
        ? `${filesystem_root}/${subdomain}/presentations/${presentation.id}/${presentation.id}/assets/themes/${slide?.theme?.label}`
        : `${filesystem_root}/${subdomain}/themes/${slide?.theme?.label}`;
      const notes = presentationJson?.notes?.[slide.id] ?? null;

      return (
        <Slide
          activeSlideIndex={indexh}
          slideIndex={index}
          key={slide.id}
          live={isLive}
          json={json}
          theme_json={theme_json}
          themeDirectory={themeDirectory}
          presentation_id={presentation?.id}
          slide={slide}
          slideNotes={notes}
        />
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preview, presentation]);

  if (isLoading) {
    return <CompanyappLoader />;
  } else {
    if (isError) {
      return (
        <Row className="h-100 justify-content-center align-self-center">
          <Col>
            <Box
              className="d-flex h-100 justify-content-center align-items-center align-content-center"
              sx={{ flexDirection: "column" }}
            >
              <Alert variant="filled" severity="error">
                {errorMessage}
              </Alert>
            </Box>
          </Col>
        </Row>
      );
    } else {
      return (
        <AssetSigsProvider presentation={presentation} isLive={!props.preview}>
        <main className="view-presentation screen">
          <Container
            fluid
            className="ps-container"
            style={backgroundColor ? { backgroundColor: backgroundColor } : {}}
          >
            <Row className="ps-row justify-content-center align-self-center">
              <Col
                className="ps-col p-0 ps-viewer"
                style={{ position: "relative" }}
              >
                <DndContext
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                  modifiers={[restrictToParentElement]}
                >
                  {additionalLoader && (
                    <Box
                      className="w-100 h-100"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: 5,
                        background: "rgba(255, 255, 255, 1)",
                      }}
                    >
                      <CompanyappLoader />
                    </Box>
                  )}

                  <div
                    className={`reveal ${props.preview ? "preview" : ""}`}
                    ref={setNodeRef}
                  >
                    <div className="slides">{memoizedSlides}</div>

                    {!noControls && !previewChanges && (
                      <RevealControls
                        reveal={reveal}
                        indexh={indexh}
                        presentation={presentation}
                        mode={props.preview ? "preview" : "view"}
                        showControls={showControls}
                        editMode={false}
                        isInIframe={isInIframe}
                        playSlideAudio={() => {
                          setPlaySlideAudio(!playSlideAudio);
                        }}
                        hasPresentationAudio={audio}
                        styles={{
                          left: `min(${
                            dimensions.width - elementDimensions.width
                          }px , ${x}px)`,
                          top: `min(${
                            dimensions.height - elementDimensions.height
                          }px, ${y}px)`,
                        }}
                        setDimensions={(size) => {
                          setElementDimensions(size);
                          if (isInitial) {
                            setX(window.innerWidth - (size.width + 30));
                            setY(size.height + 10);
                            setIsInitial(false);
                          }
                        }}
                        presentationAudioObject={audio}
                        isSpeakerView={isSpeakerView}
                      />
                    )}

                    {!isSpeakerView && (
                      <AudioPrompt
                        open={
                          (audio && !isLoading && !additionalLoader) ||
                          (slideHasAudioPrompt &&
                            !isLoading &&
                            !additionalLoader)
                        }
                        hasPresentationAudio={audio}
                        hasSlideAudio={slideHasAudioPrompt}
                      />
                    )}
                  </div>
                </DndContext>
              </Col>
            </Row>
          </Container>
        </main>
        </AssetSigsProvider>
      );
    }
  }
}

export default function ViewPresentation(props) {
  return (
    <ViewContextProvider>
      <ViewPresentationInner {...props} />
    </ViewContextProvider>
  );
}
